import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import './Tokenomics.scss';
import Header from '../Header';
import Footer from '../Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import { FaShieldAlt, FaGem, FaUsers, FaSearch, FaLock, FaClock, FaArrowRight } from 'react-icons/fa';
import {
  buildJustLockSnapshot,
  fetchJustLockLocks,
  fetchJustLockResource,
} from '../../../utils/justLock';
import {
  BASE_CHART_URL,
  BASE_DEXTOOLS_URL,
  BASE_DCKS_CONTRACT_URL,
  BASE_LOCK_END_LABEL,
  BASE_LOCK_URL,
  BASE_PAIR_LABEL,
  BASE_POOL_CONTRACT_URL,
  BASE_PROOF_SUMMARY,
} from '../../../config/siteContent';

// Lazy-load the 3D ring to avoid blocking page paint
const TokenRing3D = lazy(() => import('./TokenRing3D'));
const TOTAL_SUPPLY = 1000000000;
const FALLBACK_FOREVER_LOCKED_TOKENS = 402296722;
const FALLBACK_TOTAL_LOCKED_TOKENS = 549464187.7336735;
const LOCK_RESOURCE_ID = 'resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp';
const LOCK_RESOURCE_VIEW_URL = `https://justlock.io/resource/${LOCK_RESOURCE_ID}`;
const BASE_TREASURY_DISCLOSED_CBETH = 18;
const BASE_LOCKED_LIQUIDITY_USD = 70000;
const BASE_LOCKED_LIQUIDITY_PER_SIDE_USD = 35000;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const formatWholeTokens = (value) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 0 });
const formatUsdCompact = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 0,
  }).format(value);

// Scroll-triggered reveal hook (same pattern as Homepage)
const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const observe = useCallback(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return observer;
  }, [threshold]);

  useEffect(() => {
    const observer = observe();
    return () => observer?.disconnect();
  }, [observe]);

  return [ref, isVisible];
};

const Tokenomics = () => {
  const [lockData, setLockData] = useState(null);
  const [lockDetails, setLockDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  // Scroll reveal refs
  const [distributionRef, distributionVisible] = useScrollReveal(0.05);
  const [lockDetailsRef, lockDetailsVisible] = useScrollReveal(0.08);
  const [benefitsRef, benefitsVisible] = useScrollReveal(0.08);
  const [crossChainRef, crossChainVisible] = useScrollReveal(0.08);

  // Handle window resize
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch lock data from API
  useEffect(() => {
    const fetchLockData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchJustLockResource(LOCK_RESOURCE_ID);
        setLockData(data);

        const liveLockIds = Array.isArray(data?.lock_summary?.lock_ids)
          ? data.lock_summary.lock_ids
          : [];

        if (liveLockIds.length) {
          try {
            const details = await fetchJustLockLocks(liveLockIds);
            setLockDetails(details);
          } catch (detailsError) {
            console.warn('Failed to fetch JustLock lock details:', detailsError);
            setLockDetails([]);
          }
        } else {
          setLockDetails([]);
        }
      } catch (error) {
        console.error('Error fetching lock data:', error);
        setError('Failed to load tokenomics data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLockData();
  }, []);

  const lockSnapshot = useMemo(
    () =>
      buildJustLockSnapshot({
        resourceData: lockData,
        lockDetails,
        resourceAddress: LOCK_RESOURCE_ID,
        totalSupply: TOTAL_SUPPLY,
        fallbackTotalLocked: FALLBACK_TOTAL_LOCKED_TOKENS,
        fallbackForeverLocked: FALLBACK_FOREVER_LOCKED_TOKENS,
      }),
    [lockData, lockDetails]
  );

  const normalizedLockedTokens = lockSnapshot.totalLocked;
  const foreverLockedTokens = lockSnapshot.foreverLocked;
  const timeLockedTokens = lockSnapshot.timeLocked;
  const communityTokens = Math.max(0, TOTAL_SUPPLY - normalizedLockedTokens);

  const foreverLockedPercent = (foreverLockedTokens / TOTAL_SUPPLY) * 100;
  const timeLockedPercent = (timeLockedTokens / TOTAL_SUPPLY) * 100;
  const communityPercent = (communityTokens / TOTAL_SUPPLY) * 100;
  const totalLockedPercent = (normalizedLockedTokens / TOTAL_SUPPLY) * 100;
  const timeLockedMillions = timeLockedTokens / 1000000;
  const radixLockStatus = lockSnapshot.lockCount > 0
    ? `${lockSnapshot.lockCount} live JustLock records`
    : 'Baseline snapshot while detailed locks load';

  // Data for pie chart
  const tokenomicsData = [
    { name: 'Forever Locked', value: foreverLockedPercent, amount: foreverLockedTokens.toFixed(0), color: '#FF3B5F' },
    { name: 'Time-Locked', value: timeLockedPercent, amount: timeLockedTokens.toFixed(0), color: '#00A3FF' },
    { name: 'Circulating', value: communityPercent, amount: communityTokens.toFixed(0), color: '#00FF9D' },
  ];

  // Extract lock IDs from API response to display in the details section
  const lockIds = lockSnapshot.lockIds;

  const trustMeters = [
    { label: 'Total Locked', value: totalLockedPercent, tone: 'crimson' },
    { label: 'Forever Locked', value: foreverLockedPercent, tone: 'pink' },
    { label: 'Time-Locked', value: timeLockedPercent, tone: 'blue' },
    { label: 'Circulating Float', value: communityPercent, tone: 'green' },
  ];

  const heroProofCards = [
    {
      tone: 'crimson',
      icon: <FaLock />,
      kicker: 'Radix Supply',
      headline: `${totalLockedPercent.toFixed(2)}% locked`,
      description: lockSnapshot.lockCount > 0
        ? `${lockSnapshot.lockCount} active JustLock records currently support the Radix supply snapshot.`
        : 'Detailed JustLock records are still loading, so the page is using the current fallback snapshot.',
      meta: [
        `${formatWholeTokens(foreverLockedTokens)} forever locked`,
        `${timeLockedMillions.toFixed(2)}M time-locked`,
      ],
    },
    {
      tone: 'cyan',
      icon: <FaShieldAlt />,
      kicker: 'Base LP',
      headline: `~${formatUsdCompact(BASE_LOCKED_LIQUIDITY_USD)} liquidity locked`,
      description: `${BASE_PAIR_LABEL} is publicly locked on Base, with roughly ${formatUsdCompact(BASE_LOCKED_LIQUIDITY_PER_SIDE_USD)} per side currently shown for the live liquidity position through Team Finance.`,
      meta: [
        `~${formatUsdCompact(BASE_LOCKED_LIQUIDITY_PER_SIDE_USD)} DCKS + ~${formatUsdCompact(BASE_LOCKED_LIQUIDITY_PER_SIDE_USD)} cbETH`,
        `Team Finance through ${BASE_LOCK_END_LABEL}`,
      ],
    },
    {
      tone: 'green',
      icon: <FaGem />,
      kicker: 'Treasury LP',
      headline: 'Radix + Base tracked',
      description: 'Locked LP positions stay disclosed outside the Radix JustLock ring so supply locks and LP proof never get blended together.',
      meta: [
        'Ociswap / C9 / GBOF disclosed',
        `${BASE_TREASURY_DISCLOSED_CBETH} cbETH side disclosed on Base`,
      ],
    },
  ];

  const lockRegistryCards = [
    {
      key: 'radix-supply',
      tone: 'crimson',
      kicker: 'Canonical',
      icon: <FaLock />,
      title: 'Radix Supply / Token Locks',
      summary:
        'JustLock remains the canonical source for REDDICKS token supply lock visibility on Radix. These are the numbers used in the circular distribution view.',
      stats: [
        { label: 'Locked', value: `${totalLockedPercent.toFixed(2)}%` },
        { label: 'Records', value: `${lockSnapshot.lockCount || 0}` },
      ],
      bullets: [
        `${formatWholeTokens(foreverLockedTokens)} tokens forever locked`,
        `${formatWholeTokens(timeLockedTokens)} tokens time-locked`,
        `${radixLockStatus} on the REDDICKS resource`,
      ],
      actions: [
        { label: 'View JustLock', href: LOCK_RESOURCE_VIEW_URL, kind: 'external' },
        { label: 'Open Verify', href: '/verify', kind: 'internal' },
      ],
    },
    {
      key: 'radix-liquidity',
      tone: 'violet',
      kicker: 'Treasury',
      icon: <FaGem />,
      title: 'Radix Locked LP Context',
      summary:
        'Radix liquidity trust anchors are disclosed through treasury positions, not through the JustLock supply API. They belong in the trust map, not in the supply percentage.',
      stats: [
        { label: 'Lanes', value: '3' },
        { label: 'Scope', value: 'Radix LP' },
      ],
      bullets: [
        'Forever-locked Ociswap LP remains part of the trust story',
        'CaviarNine and GBOF LP positions remain disclosed in treasury totals',
        'LP composition is tracked separately from Radix token lock percentages',
      ],
      actions: [
        { label: 'View Treasury', href: '/treasury', kind: 'internal' },
        { label: 'Verification Guide', href: '/verify', kind: 'internal' },
      ],
    },
    {
      key: 'base-liquidity',
      tone: 'green',
      kicker: 'Base Live',
      icon: <FaShieldAlt />,
      title: 'Base LP Lock Proof',
      summary:
        'Base LP proof is live and public. Team Finance is the primary lock surface, while explorer and contract links reinforce the same lock story from separate public pages.',
      stats: [
        { label: 'Locked', value: `~${formatUsdCompact(BASE_LOCKED_LIQUIDITY_USD)}` },
        { label: 'Side Split', value: `~${formatUsdCompact(BASE_LOCKED_LIQUIDITY_PER_SIDE_USD)} / ~${formatUsdCompact(BASE_LOCKED_LIQUIDITY_PER_SIDE_USD)}` },
        { label: 'Pair', value: BASE_PAIR_LABEL },
        { label: 'Lock End', value: BASE_LOCK_END_LABEL },
      ],
      bullets: [
        `${BASE_PROOF_SUMMARY}`,
        `Approximate live Base LP visibility is ~${formatUsdCompact(BASE_LOCKED_LIQUIDITY_USD)} total, split around ${formatUsdCompact(BASE_LOCKED_LIQUIDITY_PER_SIDE_USD)} per side`,
        `${BASE_TREASURY_DISCLOSED_CBETH} cbETH is currently disclosed on the Base LP side via treasury context`,
        'Base LP proof is intentionally shown separately from the Radix JustLock supply dataset',
      ],
      actions: [
        { label: 'Team Finance', href: BASE_LOCK_URL, kind: 'external' },
        { label: 'DEXTools', href: BASE_DEXTOOLS_URL, kind: 'external' },
        { label: 'Dexscreener', href: BASE_CHART_URL, kind: 'external' },
        { label: 'Pool BaseScan', href: BASE_POOL_CONTRACT_URL, kind: 'external' },
        { label: 'Token BaseScan', href: BASE_DCKS_CONTRACT_URL, kind: 'external' },
      ],
    },
    {
      key: 'solana-lane',
      tone: 'cyan',
      kicker: 'Next Lane',
      icon: <FaClock />,
      title: 'Solana Lock Lane',
      summary:
        'Solana sits in the disclosed roadmap, but it is not being counted as a live lock proof surface yet. This card exists so users can see what is live now versus what is still pending.',
      stats: [
        { label: 'Status', value: 'Pending' },
        { label: 'Surface', value: 'Future' },
      ],
      bullets: [
        'Radix token locks are live now',
        'Base LP lock proof is live now',
        'Future REDDICKS/SOL lock links should be published before this lane is treated as live proof',
      ],
      actions: [
        { label: 'Open Verify', href: '/verify', kind: 'internal' },
        { label: 'View Treasury', href: '/treasury', kind: 'internal' },
      ],
    },
  ];

  const lockTimeline = useMemo(() => {
    const baseMilestones = [
      {
        key: 'forever',
        title: 'Forever Locked Reserve',
        detail: `${foreverLockedTokens.toLocaleString()} tokens are permanently locked.`,
        tone: 'crimson',
      },
      {
        key: 'time',
        title: 'Time-Locked Reserve',
        detail: `${timeLockedTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })} tokens currently protected by lock schedules.`,
        tone: 'blue',
      },
    ];

    const idMilestones = lockIds.length
      ? lockIds.map((id, idx) => ({
        key: `id-${id}`,
        title: `Lock Identifier #${id}`,
        detail: `On-chain lock reference ${idx + 1} in the active JustLock set.`,
        tone: idx % 2 === 0 ? 'violet' : 'cyan',
      }))
      : [
        {
          key: 'id-pending',
          title: 'Lock Identifiers Loading',
          detail: 'Live lock IDs are fetched from JustLock and may appear after the API responds.',
          tone: 'violet',
        },
      ];

    const merged = [...baseMilestones, ...idMilestones];
    return isMobile ? merged.slice(0, 6) : merged.slice(0, 10);
  }, [foreverLockedTokens, timeLockedTokens, lockIds, isMobile]);



  // Distribution stat cards data
  const distributionCards = [
    {
      icon: <FaLock />,
      label: 'Forever Locked',
      amount: foreverLockedTokens.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      percent: foreverLockedPercent,
      color: 'crimson',
      colorHex: '#FF3B5F',
      description: 'These tokens are permanently locked and can never be accessed, effectively reducing the circulating supply and increasing token scarcity.',
    },
    {
      icon: <FaClock />,
      label: 'Time-Locked',
      amount: timeLockedTokens.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      percent: timeLockedPercent,
      color: 'blue',
      colorHex: '#00A3FF',
      description: 'These tokens are locked for specific time periods, supporting phased liquidity planning and long-term project development.',
    },
    {
      icon: <FaUsers />,
      label: 'Circulating',
      amount: communityTokens.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      percent: communityPercent,
      color: 'green',
      colorHex: '#00FF9D',
      description: 'Tokens available for trading, utility, and onboarding new users from Base. Lock policy keeps the structural trust intact while supply stays accessible.',
    },
  ];

  // Benefit items data
  const benefits = [
    {
      icon: <FaShieldAlt />,
      title: 'Rug Proof POL',
      description: 'Liquidity trust is disclosed across Radix treasury LP positions and the live Base Team Finance lock. Treasury owns the LP, and 1% trading fees flow back to fund execution.',
      color: 'cyan',
      number: '01',
      offset: 'none',
    },
    {
      icon: <FaGem />,
      title: 'Token Scarcity',
      description: `${foreverLockedTokens.toLocaleString()} tokens are permanently removed from circulation, increasing token scarcity and potential value over time.`,
      color: 'crimson',
      number: '02',
      offset: 'up',
    },
    {
      icon: <FaUsers />,
      title: 'Circulating Participation',
      description: 'Circulating supply enables trading access, utility participation, and broader distribution as new users enter from Base and beyond.',
      color: 'green',
      number: '03',
      offset: 'down',
    },
    {
      icon: <FaSearch />,
      title: 'Transparency',
      description: 'Radix supply locks are verifiable through JustLock, while Base LP proof stays public through Team Finance, Dexscreener, DEXTools, and BaseScan.',
      color: 'violet',
      number: '04',
      offset: 'none',
    },
  ];

  return (
    <div className="tokenomics-page" data-scroll-root>
      <Header />

      <main className="tokenomics-content">
        {/* ============ HERO SECTION ============ */}
        <section className="tokenomics-hero">
          <div className="hero-bg-effects">
            <div className="hero-orb hero-orb--1"></div>
            <div className="hero-orb hero-orb--2"></div>
            <div className="hero-orb hero-orb--3"></div>
          </div>
          <div className="container">
            <span className="hero-tagline">On-Chain Transparency</span>
            <h1>
              <span className="hero-title__line">REDDICKS</span>
              <span className="hero-title__brand">Tokenomics</span>
            </h1>
            <p className="hero-lead">
              Start here for transparent numbers first. Then verify lock records
              and choose your trading route. Base is the entry lane, while Radix
              keeps the live utility and validator layer active. Token supply
              locks and LP lock proof are shown separately so the trust model is
              easy to inspect.
            </p>
            <div className="hero-supply">
              <span className="supply-label">Total Supply</span>
              <span className="supply-value">{TOTAL_SUPPLY.toLocaleString()}</span>
              <span className="supply-unit">Tokens</span>
            </div>
            <div className="hero-lock-highlight">
              <span className="hero-lock-highlight__label">Radix Supply Locked</span>
              <span className="hero-lock-highlight__value">{totalLockedPercent.toFixed(2)}%</span>
            </div>
            <div className="hero-proof-grid">
              {heroProofCards.map((card) => (
                <article className={`hero-proof-card hero-proof-card--${card.tone}`} key={card.kicker}>
                  <div className="hero-proof-card__head">
                    <div className="hero-proof-card__icon">{card.icon}</div>
                    <div className="hero-proof-card__copy">
                      <span className="hero-proof-card__kicker">{card.kicker}</span>
                      <strong>{card.headline}</strong>
                    </div>
                  </div>
                  <p>{card.description}</p>
                  <div className="hero-proof-card__meta">
                    {card.meta.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
            <div className="hero-actions">
              <Link to="/verify" className="hero-cta hero-cta--verify">
                Verify Liquidity <FaArrowRight />
              </Link>
              <Link to="/buy" className="hero-cta hero-cta--buy">
                Buy on Base
              </Link>
            </div>
          </div>
        </section>

        {/* ============ DISTRIBUTION SECTION ============ */}
        <section ref={distributionRef} className={`distribution-section ${distributionVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Radix Supply Distribution</h2>
            <p className="section-subtitle centered">
              This ring visualizes the REDDICKS JustLock resource on Radix only.
              Base LP proof and treasury-held LP context are shown separately in
              the lock registry below.
            </p>

            {/* 3D Token Ring */}
            <div className="distribution-showcase">
              <Suspense fallback={
                <div className="ring3d-loading">
                  <div className="spinner"></div>
                  <p>Loading 3D visualization...</p>
                </div>
              }>
                <TokenRing3D
                  tokenomicsData={tokenomicsData}
                  totalLockedPercent={totalLockedPercent}
                  loading={loading}
                  error={error}
                />
              </Suspense>
            </div>

            {/* Stat cards strip below chart */}
            <div className="distribution-cards">
              {distributionCards.map((card, index) => (
                <div className={`dist-card dist-card--${card.color}`} key={index}>
                  <div className="dist-card__header">
                    <div className="dist-card__icon">
                      {card.icon}
                    </div>
                    <div className="dist-card__meta">
                      <span className="dist-card__label">{card.label}</span>
                      <span className="dist-card__percent">{card.percent.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="dist-card__amount">{card.amount}</div>
                  <div className="dist-card__bar">
                    <div
                      className="dist-card__bar-fill"
                      style={{
                        width: distributionVisible ? `${clamp(card.percent, 0, 100)}%` : '0%',
                        backgroundColor: card.colorHex,
                      }}
                    ></div>
                  </div>
                  <p className="dist-card__desc">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ BENEFITS SECTION ============ */}
        <section ref={benefitsRef} className={`tokenomics-benefits-section ${benefitsVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="benefits-bg-effects">
            <div className="benefits-orb benefits-orb--1"></div>
            <div className="benefits-orb benefits-orb--2"></div>
            <div className="benefits-grid-lines"></div>
          </div>
          <div className="container">
            <h2 className="section-title centered">Benefits of REDDICKS Tokenomics</h2>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <div
                  className={`benefit benefit--${benefit.color} benefit--offset-${benefit.offset}`}
                  key={index}
                  style={{ animationDelay: `${0.15 * index}s` }}
                >
                  <div className="benefit__glow"></div>
                  <div className="benefit__inner">
                    <div className="benefit__content">
                      <div className="benefit__icon-container">
                        <div className="benefit__icon-bg"></div>
                        <div className="benefit__icon-wrap">
                          {benefit.icon}
                        </div>
                      </div>
                      <div className="benefit__text">
                        <span className="benefit__number">{benefit.number}</span>
                        <h3>{benefit.title}</h3>
                        <p>{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ LOCK DETAILS SECTION ============ */}
        <section ref={lockDetailsRef} className={`locked-details-section ${lockDetailsVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Radix Lock Details</h2>
            <div className="trust-meter-grid">
              {trustMeters.map((meter) => (
                <div className={`trust-meter trust-meter--${meter.tone}`} key={meter.label}>
                  <div className="trust-meter__head">
                    <span>{meter.label}</span>
                    <strong>{meter.value.toFixed(2)}%</strong>
                  </div>
                  <div className="trust-meter__track">
                    <div className="trust-meter__fill" style={{ width: `${clamp(meter.value, 0, 100)}%` }}></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lock-timeline">
              {lockTimeline.map((item) => (
                <div className={`lock-timeline__item lock-timeline__item--${item.tone}`} key={item.key}>
                  <span className="lock-timeline__dot"></span>
                  <div className="lock-timeline__body">
                    <h4>{item.title}</h4>
                    <p>{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="details-content">
              <div className="details-info">
                <p>
                  REDDICKS takes token security seriously. A significant portion of supply is locked
                  through JustLock, with live records sourced from the protocol API.
                </p>
                <p>
                  With <strong>{foreverLockedTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })} tokens forever locked</strong> and over <strong>{timeLockedMillions.toFixed(2)} million tokens in time-based locks</strong>, REDDICKS keeps a transparent lock profile.
                </p>
                <p>
                  {lockSnapshot.lockCount > 0
                    ? `Breakdown is currently derived from ${lockSnapshot.lockCount} active lock records.`
                    : 'Breakdown currently falls back to baseline values while detailed lock records load.'}
                </p>
                <a
                  href={LOCK_RESOURCE_VIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="verify-button"
                >
                  Verify on JustLock <FaArrowRight className="btn-arrow" />
                </a>
              </div>

              <div className="lock-ids-container">
                <h3>Lock Identifiers</h3>
                <div className="lock-ids-grid">
                  {lockIds.length ? (
                    lockIds.map((id, idx) => (
                      <div key={`${id}-${idx}`} className="lock-id">
                        #{id}
                      </div>
                    ))
                  ) : (
                    <div className="lock-id lock-id-empty">Live lock IDs unavailable</div>
                  )}
                </div>
                <div className="lock-ids-note">
                  Each lock ID represents a separate token locking transaction on the Radix blockchain.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section ref={crossChainRef} className={`cross-chain-trust-section ${crossChainVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Complete Lock Map</h2>
            <p className="cross-chain-trust__lead">
              Everything currently disclosed sits here: Radix supply locks,
              Radix treasury-held LP trust anchors, the live Base Team Finance
              lock, and the next pending lane. Different proof surfaces stay
              separated on purpose so each one remains honest about what it proves.
            </p>

            <div className="cross-chain-trust__grid">
              {lockRegistryCards.map((card) => (
                <article className={`cross-chain-trust-card cross-chain-trust-card--${card.tone}`} key={card.key}>
                  <span className="cross-chain-trust-card__kicker">{card.kicker}</span>
                  <div className="cross-chain-trust-card__header">
                    <div className="cross-chain-trust-card__icon">{card.icon}</div>
                    <div className="cross-chain-trust-card__title-wrap">
                      <h3>{card.title}</h3>
                      <p>{card.summary}</p>
                    </div>
                  </div>
                  <div className="cross-chain-trust-card__stats">
                    {card.stats.map((item) => (
                      <div className="cross-chain-trust-card__stat" key={`${card.key}-${item.label}`}>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                    ))}
                  </div>
                  <ul>
                    {card.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <div className="cross-chain-trust-card__actions">
                    {card.actions.map((action) => (
                      action.kind === 'internal' ? (
                        <Link to={action.href} key={`${card.key}-${action.label}`}>
                          {action.label}
                        </Link>
                      ) : (
                        <a href={action.href} target="_blank" rel="noopener noreferrer" key={`${card.key}-${action.label}`}>
                          {action.label}
                        </a>
                      )
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Tokenomics;
